// ============================================================
// Aerodock Theme & Core Interactivity
// ============================================================



// Announcement bar carousel
function initAnnouncementBars(root = document) {
  root.querySelectorAll('.ann').forEach(bar => {
    if (bar.dataset.initialized) return;
    bar.dataset.initialized = 'true';

    const slides = bar.querySelectorAll('.ann-slide');
    if (!slides.length) return;

    if (slides.length === 1 || bar.dataset.static === 'true') {
      slides[0].classList.add('is-active');
      return;
    }

    let current = 0;
    const interval = parseInt(bar.dataset.interval, 10) || 5000;
    slides[0].classList.add('is-active');

    const timer = setInterval(() => {
      const prev = slides[current];
      current = (current + 1) % slides.length;
      const next = slides[current];

      prev.classList.remove('is-active');
      prev.classList.add('is-exiting');
      next.classList.add('is-active');

      setTimeout(() => prev.classList.remove('is-exiting'), 500);
    }, interval);
    
    bar.dataset.timerId = timer;
  });
}

// Native account disclosure also works before JavaScript loads.
function initAccountMenus(root = document) {
  root.querySelectorAll('[data-account-menu]').forEach(menu => {
    if (menu.dataset.initialized) return;
    menu.dataset.initialized = 'true';
    const trigger = menu.querySelector('summary');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const close = (restoreFocus = false) => {
      menu.open = false;
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    };
    trigger.setAttribute('aria-expanded', String(menu.open));
    menu.addEventListener('toggle', () => {
      trigger.setAttribute('aria-expanded', String(menu.open));
    }, options);
    menu.querySelector('[data-account-close]').addEventListener('click', () => close(true), options);
    document.addEventListener('click', event => {
      if (menu.open && !menu.contains(event.target)) close();
    }, options);
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    }, options);
    menu.addEventListener('focusout', event => {
      if (event.relatedTarget && !menu.contains(event.relatedTarget)) close();
    }, options);
    menu.cleanupAccountMenu = () => controller.abort();
  });
}

// Keep category links and search from competing for the same space.
function initCategoryNavigation(root = document) {
  root.querySelectorAll('.categories-dropdown').forEach(dropdown => {
    if (dropdown.dataset.initialized) return;
    dropdown.dataset.initialized = 'true';
    const bar = dropdown.closest('.nav-sub');
    const trigger = dropdown.querySelector('.categories-btn');
    const links = dropdown.querySelector('.cat-level-1');
    const search = bar.querySelector('.search-wrap');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const setOpen = open => {
      dropdown.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', String(open));
    };
    const updateLayout = () => {
      if (controller.signal.aborted) return;
      const wasCompact = dropdown.classList.contains('is-compact');
      dropdown.classList.remove('is-compact');
      const styles = getComputedStyle(bar);
      const available = bar.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      const required = links.getBoundingClientRect().width + search.getBoundingClientRect().width + parseFloat(styles.columnGap);
      const compact = window.innerWidth < 1280 || required > available;
      dropdown.classList.toggle('is-compact', compact);
      if (!compact || compact !== wasCompact) setOpen(false);
    };
    trigger.addEventListener('click', () => setOpen(!dropdown.classList.contains('is-open')), options);
    document.addEventListener('click', event => {
      if (!dropdown.contains(event.target)) setOpen(false);
    }, options);
    dropdown.addEventListener('keydown', event => {
      if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
        event.preventDefault();
        setOpen(false);
        trigger.focus();
      }
    }, options);
    dropdown.addEventListener('focusout', event => {
      if (event.relatedTarget && !dropdown.contains(event.relatedTarget)) setOpen(false);
    }, options);
    window.addEventListener('resize', updateLayout, options);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(updateLayout) : null;
    observer?.observe(bar);
    updateLayout();
    document.fonts?.ready.then(updateLayout);
    dropdown.cleanupCategoryNavigation = () => {
      controller.abort();
      observer?.disconnect();
    };
  });
}

// Search modal and platform-specific keyboard shortcut
function initSearch(root = document) {
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  root.querySelectorAll('[data-search]').forEach(search => {
    if (search.dataset.initialized) return;
    const dialog = search.querySelector('.search-modal');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    search.dataset.initialized = 'true';

    const input = search.querySelector('.search-input');
    const modalInput = search.querySelector('.search-modal-input');
    const toggle = search.querySelector('.search-toggle');
    const hint = search.querySelector('.search-shortcut');
    const results = search.querySelector('.search-results');
    const status = search.querySelector('.search-status');
    const clearButton = search.querySelector('.search-modal-clear');
    let restoringFocus = false;
    let searchTimer;
    let searchController;
    let searchVersion = 0;

    function cancelSearchRequest() {
      clearTimeout(searchTimer);
      searchController?.abort();
      searchVersion += 1;
      results.setAttribute('aria-busy', 'false');
    }

    function updateResults() {
      cancelSearchRequest();
      results.replaceChildren();
      const query = modalInput.value.trim();
      clearButton.hidden = modalInput.value.length === 0;
      if (!query) {
        status.textContent = 'Start typing to find products.';
        return;
      }
      status.textContent = 'Searching…';
      results.setAttribute('aria-busy', 'true');
      const version = searchVersion;
      searchTimer = setTimeout(async () => {
        searchController = new AbortController();
        try {
          const url = new URL(search.dataset.predictiveSearchUrl, window.location.origin);
          url.search = new URLSearchParams({
            q: query,
            'resources[type]': 'product',
            'resources[limit]': '6',
            section_id: 'predictive-search'
          }).toString();
          const response = await fetch(url, { signal: searchController.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const markup = await response.text();
          if (version !== searchVersion || !dialog.open) return;
          const predictions = new DOMParser().parseFromString(markup, 'text/html')
            .querySelector('.search-predictions');
          if (!predictions) throw new Error('Missing search results');
          results.replaceChildren(...predictions.childNodes);
          const count = Number(predictions.dataset.resultCount);
          status.textContent = count > 0
            ? `${count} product${count === 1 ? '' : 's'} found.`
            : `No products found for “${query}”.`;
        } catch (error) {
          if (error.name === 'AbortError' || version !== searchVersion || !dialog.open) return;
          status.textContent = 'Live results are unavailable. Press Enter to search.';
        } finally {
          if (version === searchVersion) results.setAttribute('aria-busy', 'false');
        }
      }, 200);
    }

    modalInput.addEventListener('input', event => {
      if (!event.isComposing) updateResults();
    });
    modalInput.addEventListener('compositionstart', cancelSearchRequest);
    modalInput.addEventListener('compositionend', updateResults);
    clearButton.addEventListener('click', () => {
      modalInput.value = '';
      updateResults();
      modalInput.focus();
    });
    search.cancelSearchRequest = cancelSearchRequest;

    hint.textContent = isMac ? '⌘ K' : 'Ctrl K';
    hint.hidden = false;
    hint.parentElement.classList.add('has-shortcut');
    [input, toggle].forEach(trigger => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', dialog.id);
      trigger.setAttribute('aria-keyshortcuts', isMac ? 'Meta+K' : 'Control+K');
    });

    function openSearch() {
      if (restoringFocus) return;
      if (!dialog.open) {
        modalInput.value = input.value;
        dialog.showModal();
        updateResults();
      }
      modalInput.focus();
    }

    function closeSearch() {
      cancelSearchRequest();
      input.value = modalInput.value;
      restoringFocus = true;
      dialog.close();
      queueMicrotask(() => { restoringFocus = false; });
    }

    search.openSearch = openSearch;
    input.addEventListener('focus', openSearch);
    input.addEventListener('click', openSearch);
    search.querySelector('.search-form').addEventListener('submit', event => {
      event.preventDefault();
      openSearch();
    });
    // The empty header field would otherwise fail validation before submit.
    toggle.addEventListener('click', event => {
      event.preventDefault();
      openSearch();
    });
    search.querySelector('.search-modal-close').addEventListener('click', closeSearch);
    dialog.addEventListener('keydown', event => {
      if (!event.isComposing && !event.altKey && !event.ctrlKey && !event.metaKey &&
          (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        const links = Array.from(results.querySelectorAll('.search-result-link'));
        const index = links.indexOf(event.target);
        if (links.length && (event.target === modalInput || index !== -1)) {
          event.preventDefault();
          const next = event.target === modalInput
            ? (event.key === 'ArrowDown' ? 0 : links.length - 1)
            : index + (event.key === 'ArrowDown' ? 1 : -1);
          (links[next] || modalInput).focus();
        }
      }
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
      closeSearch();
    });
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      closeSearch();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) closeSearch();
    });
  });
}

document.addEventListener('keydown', event => {
  if (event.defaultPrevented || event.isComposing || event.repeat ||
      event.altKey || event.shiftKey || !(event.ctrlKey || event.metaKey) ||
      event.key.toLowerCase() !== 'k') return;
  const search = document.querySelector('[data-search]');
  if (!search?.openSearch) return;
  event.preventDefault();
  search.openSearch();
});

// Custom Interactive Cursor
const curEl = document.getElementById('cur');
const ringEl = document.getElementById('curRing');
if (curEl && ringEl && (!window.Shopify || !window.Shopify.designMode)) {
  let mx = 0, my = 0, rx = 0, ry = 0;
  document.addEventListener('mousemove', e => {
    mx = e.clientX;
    my = e.clientY;
    curEl.style.left = mx + 'px';
    curEl.style.top = my + 'px';
  });
  
  (function ar() {
    rx += (mx - rx) * 0.13;
    ry += (my - ry) * 0.13;
    ringEl.style.left = rx + 'px';
    ringEl.style.top = ry + 'px';
    requestAnimationFrame(ar);
  })();

  const hoverTargets = 'a, button, .pc, .cc, .bopt, .bstep, .qty-btn, .addbtn, .theme-btn';
  document.addEventListener('mouseover', e => {
    if (e.target.closest(hoverTargets)) {
      curEl.style.transform = 'translate(-50%,-50%) scale(2.5)';
      ringEl.style.width = '44px';
      ringEl.style.height = '44px';
    }
  });
  document.addEventListener('mouseout', e => {
    if (!e.target.closest(hoverTargets)) {
      curEl.style.transform = 'translate(-50%,-50%) scale(1)';
      ringEl.style.width = '30px';
      ringEl.style.height = '30px';
    }
  });
} else if (curEl && ringEl) {
  curEl.style.display = 'none';
  ringEl.style.display = 'none';
}

// Intersection Observer for Reveal animations
const revealObserver = new IntersectionObserver(entries => {
  entries.forEach(e => {
    if (e.isIntersecting) {
      e.target.classList.add('vis');
      revealObserver.unobserve(e.target);
    }
  });
}, { threshold: 0.08 });

function initReveal(root = document) {
  const selector = '.rev, [data-footer-reveal]';
  if (root.matches && root.matches(selector)) revealObserver.observe(root);
  root.querySelectorAll(selector).forEach(el => revealObserver.observe(el));
}
initReveal();


// ============================================================
// Shopify AJAX Cart Operations
// ============================================================
let currentCart = null;

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const parts = amount.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return '₹' + parts[0];
}

window.openCart = function() {
  document.getElementById('cSb')?.classList.add('open');
  document.getElementById('cOv')?.classList.add('open');
};

window.closeCart = function() {
  document.getElementById('cSb')?.classList.remove('open');
  document.getElementById('cOv')?.classList.remove('open');
};

window.refreshCart = async function() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    currentCart = cart;

    // Update Item Count on Header & Drawer
    const counts = document.querySelectorAll('.cart-count');
    counts.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    
    const drawerTitleCount = document.getElementById('cHdN');
    if (drawerTitleCount) {
      drawerTitleCount.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
    }

    const cartItemsContainer = document.getElementById('cItems');
    const footerContainer = document.getElementById('cFt');

    if (cart.item_count === 0) {
      if (cartItemsContainer) {
        cartItemsContainer.innerHTML = `
          <div class="c-empty">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M3 3h2l.4 2M5.4 5h15l-1.5 7H6.4M5.4 5l2 10h12" stroke="currentColor" stroke-width="1.5"/><circle cx="9" cy="20" r="1.5" fill="currentColor"/><circle cx="17" cy="20" r="1.5" fill="currentColor"/></svg>
            <p>Your cart is empty</p>
          </div>
        `;
      }
      if (footerContainer) footerContainer.style.display = 'none';
      return;
    }

    // Render Cart Items
    if (cartItemsContainer) {
      cartItemsContainer.innerHTML = cart.items.map(item => `
        <div class="citem">
          <img class="citem-img" src="${item.image || 'https://cdn.shopify.com/s/images/themes/product-1.png'}" alt="${item.title}">
          <div class="citem-info">
            <div class="citem-cat">${item.product_type || 'Component'}</div>
            <div class="citem-name">${item.product_title}</div>
            <div class="citem-price">${formatMoney(item.price)}</div>
            <div class="citem-qty">
              <button class="qty-btn" onclick="updateQty('${item.key}', ${item.quantity - 1})">-</button>
              <span class="qty-val">${item.quantity}</span>
              <button class="qty-btn" onclick="updateQty('${item.key}', ${item.quantity + 1})">+</button>
            </div>
          </div>
          <button class="citem-del" onclick="updateQty('${item.key}', 0)">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
      `).join('');
    }

    // Recalculate Subtotal, GST, Grand Total
    const subtotal = cart.total_price;
    const gst = Math.round(subtotal * 0.18);
    const total = subtotal + gst;

    const subtotalEl = document.getElementById('cSub');
    const gstEl = document.getElementById('cGst');
    const totalEl = document.getElementById('cTot');

    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    if (gstEl) gstEl.textContent = formatMoney(gst);
    if (totalEl) totalEl.textContent = formatMoney(total);

    if (footerContainer) footerContainer.style.display = 'block';
  } catch (err) {
    console.error('Failed to refresh cart:', err);
  }
};

window.addItemToCart = async function(variantId, qty = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: qty }]
      })
    });
    if (response.ok) {
      await refreshCart();
      openCart();
    }
  } catch (err) {
    console.error('Failed to add item to cart:', err);
  }
};

window.updateQty = async function(itemKey, qty) {
  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: qty
      })
    });
    if (response.ok) {
      refreshCart();
    }
  } catch (err) {
    console.error('Failed to update quantity:', err);
  }
};

// WhatsApp Cart Share
window.sendCartWA = function() {
  if (!currentCart || currentCart.item_count === 0) return;
  const waNum = window.theme.whatsappNumber || '918076592237';
  const subtotal = currentCart.total_price;
  const gst = Math.round(subtotal * 0.18);
  const grandTotal = subtotal + gst;

  let msg = '🛒 *Aerodock Order Enquiry*\n\n';
  currentCart.items.forEach(item => {
    msg += `• ${item.title} ×${item.quantity} — ${formatMoney(item.price * item.quantity)}\n`;
  });
  msg += `\n*Subtotal:* ${formatMoney(subtotal)}`;
  msg += `\n*GST (18%):* ${formatMoney(gst)}`;
  msg += `\n*Total:* ${formatMoney(grandTotal)}\n\naerodock.in`;

  window.open(`https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`, '_blank');
};

window.openWA = function() {
  const waNum = window.theme.whatsappNumber || '918076592237';
  window.open(`https://wa.me/${waNum}?text=${encodeURIComponent('Hello Aerodock, a quick question!')}`, '_blank');
};

// ============================================================
// Build Configurator Options
// ============================================================
window.selBuild = function(el, price) {
  const optsContainer = el.closest('.bopts');
  if (optsContainer) {
    optsContainer.querySelectorAll('.bopt').forEach(opt => opt.classList.remove('sel'));
  }
  el.classList.add('sel');
  
  // Calculate Build Total based on selected options
  let total = 0;
  document.querySelectorAll('.bopt.sel').forEach(opt => {
    const valText = opt.querySelector('.bop')?.textContent || '₹0';
    const cleanVal = parseInt(valText.replace(/[^0-9]/g, '')) || 0;
    total += cleanVal;
  });

  const totalEl = document.getElementById('buildTotal');
  if (totalEl) {
    // Format build total as currency
    totalEl.textContent = '₹' + total.toLocaleString('en-IN');
  }
};

// ============================================================
// AI CHATBOT
// ============================================================
let chatOpen = false;
const BOT = {
  motor: 'For 5" freestyle I recommend the **EMAX ECOII-2207 2400KV** (₹2,800) — punchy, durable, and proven. For long-range or efficiency builds, the **T-Motor 2207 V2 1750KV** (₹3,400) is the go-to. Want to add either to your cart?',
  esc: 'The **SpeedyBee F405 V3 Stack** includes a matched 50A BLS ESC. For separate ESC pairings, any 30×30 4-in-1 under 50A works. The stack is currently ₹6,800 — best value for a matched combo.',
  dgca: 'DGCA rules in India:\n• Drones >250g need UIN registration on Digital Sky\n• Max altitude 400ft AGL in most areas\n• No-fly within 5km of airports\n• Green/Yellow/Red zone maps on Digital Sky portal\nAll Aerodock products are sold for compliant use only.',
  prop: 'For efficiency: **HQProp 7X4X3** (₹280) — lower pitch, more flight time. For power/freestyle: **GEMFAN 7050 Hurricane** (₹320) — aggressive 3-blade, snappy feel. Both in stock!',
  default: 'Great question! I specialise in FPV builds and India DGCA regulations. Ask me about motors, ESCs, cameras, propellers, or compliance — or I can connect you to WhatsApp pilot support for complex queries. 🚁'
};

window.getBotReply = function(m) {
  const l = m.toLowerCase();
  if (l.includes('motor') || l.includes('kv') || l.includes('freestyle') || l.includes('emax') || l.includes('tmotor')) return BOT.motor;
  if (l.includes('esc') || l.includes('speedybee') || l.includes('f405') || l.includes('compatible')) return BOT.esc;
  if (l.includes('dgca') || l.includes('register') || l.includes('legal') || l.includes('compliance') || l.includes('uin')) return BOT.dgca;
  if (l.includes('prop') || l.includes('propeller') || l.includes('gemfan') || l.includes('hq') || l.includes('efficient')) return BOT.prop;
  return BOT.default;
};

window.toggleChat = function() {
  chatOpen = !chatOpen;
  document.getElementById('chatPanel')?.classList.toggle('open', chatOpen);
};

window.addMsg = function(text, role) {
  const el = document.getElementById('chatMsgs');
  if (!el) return;
  const t = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const d = document.createElement('div');
  d.className = `msg ${role}`;
  d.innerHTML = `<div class="msg-bbl">${text.replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/\n/g,'<br>')}</div><div class="msg-time">${t}</div>`;
  el.appendChild(d);
  el.scrollTop = el.scrollHeight;
};

window.sendMsg = async function() {
  const inp = document.getElementById('chatInp');
  if (!inp) return;
  const val = inp.value.trim();
  if (!val) return;
  inp.value = '';
  
  const suggestionsPanel = document.getElementById('chatSugs');
  if (suggestionsPanel) suggestionsPanel.style.display = 'none';
  
  addMsg(val, 'user');

  // Typing indicator
  const el = document.getElementById('chatMsgs');
  if (!el) return;
  const typing = document.createElement('div');
  typing.className = 'msg bot';
  typing.id = 'typing';
  typing.innerHTML = '<div class="typing-ind"><span></span><span></span><span></span></div>';
  el.appendChild(typing);
  el.scrollTop = el.scrollHeight;

  await new Promise(r => setTimeout(r, 700 + Math.random() * 600));
  document.getElementById('typing')?.remove();

  // Try API / Web service or fallback
  addMsg(getBotReply(val), 'bot');
};

window.useSug = function(btn) {
  const inp = document.getElementById('chatInp');
  if (inp) {
    inp.value = btn.textContent;
    sendMsg();
  }
};

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBars();
  initSearch();
  initAccountMenus();
  initCategoryNavigation();
  refreshCart();
});

// Shopify Theme Editor Integrations
document.addEventListener('shopify:section:load', (e) => {
  initAnnouncementBars(e.target);
  initSearch(e.target);
  initAccountMenus(e.target);
  initCategoryNavigation(e.target);
  initReveal(e.target);
});

document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.categories-dropdown').forEach(dropdown => dropdown.cleanupCategoryNavigation?.());
  e.target.querySelectorAll('[data-account-menu]').forEach(menu => menu.cleanupAccountMenu?.());
  e.target.querySelectorAll('[data-search]').forEach(search => search.cancelSearchRequest?.());
  e.target.querySelectorAll('.ann').forEach(bar => {
    if (bar.dataset.timerId) clearInterval(bar.dataset.timerId);
  });
});
